import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ClientOrderItemVm, remainingAmount, unshippedAmount } from '../../models/client-order.view-model';
import { formatAmount } from '../../../../../utils/format-amount';

/**
 * The flow picture of one zamówienie: zlecenia → pozycje → wysyłki, one node per entity,
 * amounts on the edges. Three columns of fixed-height node cards with an SVG in each
 * column gap; every edge endpoint is arithmetic over row indexes, so nothing is measured
 * after render. Read-only — creating and unlinking stay in the coverage rows below.
 *
 * A dumb component fed by the page store's positions (roots only — children carry no
 * allocations or shipment lines). The highlight is local state, not store state.
 */
@Component({
  selector: 'client-order-flow',
  templateUrl: './client-order-flow.component.html',
  styleUrl: './client-order-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink]
})
export class ClientOrderFlowComponent {
  readonly positions = input.required<ClientOrderItemVm[]>();

  readonly layout = computed(() => buildFlowLayout(this.positions()));

  // click a position → its edges and neighbours light up, everything else steps back
  readonly selectedPositionId = signal<string | undefined>(undefined);

  readonly connectedOrderIds = computed(() => connectedTo(this.layout().productionEdges, this.selectedPositionId()));

  readonly connectedShipmentIds = computed(() => connectedTo(this.layout().shippingEdges, this.selectedPositionId()));

  readonly gapWidth = GAP_WIDTH;

  formatAmount = formatAmount;

  togglePosition(positionId: string): void {
    this.selectedPositionId.update(selected => selected === positionId ? undefined : positionId);
  }
}

// --- geometry: fixed row pitch, fixed gap width ---

export const NODE_HEIGHT = 66;
export const ROW_GAP = 12;
export const ROW_HEIGHT = NODE_HEIGHT + ROW_GAP;
export const GAP_WIDTH = 64;

// labels sit mid-edge; two edges crossing at the same point push the later label down a line
const LABEL_STEP = 11;

export interface FlowLayout {
  orders: OrderNode[];
  positions: PositionNode[];
  shipments: ShipmentNode[];
  productionEdges: FlowEdge[];
  shippingEdges: FlowEdge[];
  /**
   * The tallest column's height — the two edge overlays are exactly this high.
   */
  height: number;
}

export interface PositionNode {
  id: string;
  name: string;
  unit: string;
  amount: number;
  allocatedAmount: number;
  shippedAmount: number;
  /**
   * Under-covered: no zlecenie makes part of it yet.
   */
  open: boolean;
  /**
   * Fully shipped — the position is closed; a whole order of closed positions is done.
   */
  closed: boolean;
}

export interface OrderNode {
  orderId: number;
  orderNumber: string;
  statusLabel: string;
  windowLabel: string | null;
  /**
   * "ZK-02/2026, ZK-05/2026" — the other zamówienia the zlecenie covers, or null.
   */
  alsoCoversLabel: string | null;
}

export interface ShipmentNode {
  shipmentId: number;
  shipmentNumber: string;
  transport: string;
  statusLabel: string;
  planned: boolean;
}

export interface FlowEdge {
  key: string;
  positionId: string;
  /**
   * The zlecenie on a production edge, the wysyłka on a shipping edge.
   */
  targetId: number;
  amount: number;
  unit: string;
  /**
   * A line of a Planowana wysyłka — drawn dashed; production edges are never dashed.
   */
  planned: boolean;
  path: string;
  labelX: number;
  labelY: number;
}

/**
 * Nodes stack from row 0 in each column: positions in order, zlecenia and wysyłki by first
 * appearance (which keeps most edges short). De-duplicated by orderId / shipmentId — a
 * zlecenie covering two positions is one node with two incoming edges.
 */
export function buildFlowLayout(positions: ClientOrderItemVm[]): FlowLayout {
  const orders: OrderNode[] = [];
  const orderRows = new Map<number, number>();
  const shipments: ShipmentNode[] = [];
  const shipmentRows = new Map<number, number>();
  const productionEdges: FlowEdge[] = [];
  const shippingEdges: FlowEdge[] = [];
  const productionLabels = new Set<number>();
  const shippingLabels = new Set<number>();

  positions.forEach((position, positionRow) => {
    for (const allocation of position.data.allocations) {
      let orderRow = orderRows.get(allocation.orderId);

      if (orderRow === undefined) {
        orderRow = orders.length;
        orderRows.set(allocation.orderId, orderRow);
        orders.push({
          orderId: allocation.orderId,
          orderNumber: allocation.orderNumber,
          statusLabel: allocation.statusLabel,
          windowLabel: allocation.windowLabel,
          alsoCoversLabel: allocation.alsoCovers.length > 0 ? allocation.alsoCovers.join(', ') : null
        });
      }

      productionEdges.push(edge(`a${allocation.allocationId}`, position.id, allocation.orderId, allocation.amount, position.data.unit, false, orderRow, positionRow, productionLabels));
    }

    for (const line of position.data.shipments) {
      let shipmentRow = shipmentRows.get(line.shipmentId);

      if (shipmentRow === undefined) {
        shipmentRow = shipments.length;
        shipmentRows.set(line.shipmentId, shipmentRow);
        shipments.push({
          shipmentId: line.shipmentId,
          shipmentNumber: line.shipmentNumber,
          transport: line.transport,
          statusLabel: line.statusLabel,
          planned: line.planned
        });
      }

      shippingEdges.push(edge(`s${line.shipmentId}-${position.id}`, position.id, line.shipmentId, line.amount, position.data.unit, line.planned, positionRow, shipmentRow, shippingLabels));
    }
  });

  const rows = Math.max(positions.length, orders.length, shipments.length);

  return {
    orders,
    positions: positions.map(position => ({
      id: position.id,
      name: position.data.name,
      unit: position.data.unit,
      amount: position.data.amount,
      allocatedAmount: position.data.allocatedAmount,
      shippedAmount: position.data.shippedAmount,
      open: remainingAmount(position) > 0,
      closed: position.data.amount > 0 && unshippedAmount(position) <= 0 && position.data.shippedAmount >= position.data.amount
    })),
    shipments,
    productionEdges,
    shippingEdges,
    height: rows * ROW_HEIGHT
  };
}

/**
 * A cubic from the left column's row to the right column's row across the gap, the
 * label at its middle.
 */
function edge(key: string, positionId: string, targetId: number, amount: number, unit: string, planned: boolean, fromRow: number, toRow: number, usedLabels: Set<number>): FlowEdge {
  const fromY = rowCenter(fromRow);
  const toY = rowCenter(toRow);
  const middle = GAP_WIDTH / 2;

  let labelY = Math.round((fromY + toY) / 2);

  while (usedLabels.has(labelY)) {
    labelY += LABEL_STEP;
  }

  usedLabels.add(labelY);

  return {
    key,
    positionId,
    targetId,
    amount,
    unit,
    planned,
    path: `M 0 ${fromY} C ${middle} ${fromY}, ${middle} ${toY}, ${GAP_WIDTH} ${toY}`,
    labelX: middle,
    labelY
  };
}

function rowCenter(row: number): number {
  return row * ROW_HEIGHT + NODE_HEIGHT / 2;
}

function connectedTo(edges: FlowEdge[], positionId: string | undefined): Set<number> {
  return new Set(edges.filter(edge => edge.positionId === positionId).map(edge => edge.targetId));
}
